'use client'

// Chrome/Safari/Firefox "Translate this page" rewrites text nodes inside
// React-managed DOM; on the next render React calls removeChild/insertBefore on
// nodes the translator has moved and throws NotFoundError, crashing the whole
// tree (Sentry HT-APP-72 — real non-English users: ja/zh/ko/es/fr on Chrome).
//
// This is the widely-used community guard: make the two mutators a no-op when
// the target isn't actually a child of `this`, instead of throwing. Normal
// renders are unaffected — `parentNode` only mismatches once an external agent
// has already moved the node, so this changes behaviour only on the exact path
// that was crashing. Applied once at client module load, before React commits.
const patchDomMutators = (): void => {
  if (typeof window === 'undefined' || typeof Node !== 'function' || !Node.prototype) {
    return
  }
  const flagged = window as unknown as { __htTranslateDomGuard?: boolean }
  if (flagged.__htTranslateDomGuard) {
    return
  }
  flagged.__htTranslateDomGuard = true

  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child
    }
    return originalRemoveChild.call(this, child) as T
  }

  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function <T extends Node>(this: Node, node: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return node
    }
    return originalInsertBefore.call(this, node, referenceNode) as T
  }
}

patchDomMutators()

// Renders nothing — mounted in the root layout only so its module (the patch
// above) is pulled into the main client bundle and runs early.
export const TranslateDomGuard = (): null => null
